import { apiClient } from '@borradh-workspace/api-client';
import { conversationMessageAtomSchema } from '@borradh-workspace/contracts';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ComposerDecision } from '../composer-gate-context';
import { DecisionFooter } from './decision-footer';

import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

/**
 * `DraftReplyCard` — renders a customer-conversation reply DRAFT for the
 * operator to review and send.
 *
 * Q21a (locked decision): Claire-Owner never auto-sends a customer message.
 * The card surfaces the draft + three actions:
 *   - **Send** → POSTs to `conversations/:id/messages` (the existing
 *     operator-send endpoint), then marks the tool output as `approved`
 *     so Claire knows the operator accepted the draft.
 *   - **Edit** → opens an in-place textarea so the operator can revise the
 *     wording before sending (still goes through the same endpoint on send).
 *   - **Discard** → marks the tool output as `rejected` so Claire knows to
 *     suggest something different.
 *
 * The outcome is reported back to Claire as a MESSAGE. It used to be an
 * `addToolOutput({ output: 'approved' })` call, which required the tool to be
 * paused awaiting a browser answer — this one executes on the server and
 * streams an object, so the card's own "already answered" branch compared an
 * object to the string `'approved'` and rendered "Discarded" over a draft
 * nobody had discarded.
 */
interface DraftReplyCardProps {
  id?: string;
  conversationId: string;
  draft: string;
  customerName?: string | null;
  platform?: string;
  /** Sends the operator's outcome back into the conversation. */
  onRespond: (text: string) => void;
}

export function DraftReplyCard({
  id,
  conversationId,
  draft: initialDraft,
  customerName,
  platform,
  onRespond,
}: DraftReplyCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialDraft);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  const [discarded, setDiscarded] = useState(false);

  const handleSend = async () => {
    if (sending || sent || discarded || !draft.trim()) return;
    setSending(true);
    try {
      const result = await apiClient.post(
        `conversations/${conversationId}/messages`,
        {
          content: draft,
        },
        {
          schema: conversationMessageAtomSchema.pick({
            externalMessageId: true,
          }),
        }
      );
      setSent(true);
      if (result.externalMessageId) {
        toast.success('Reply sent');
        onRespond(
          `The reviewed reply was already sent from the card. Do not send it again. Final text: ${JSON.stringify(draft)}`
        );
      } else {
        setDeliveryFailed(true);
        toast.error('Reply saved, but delivery was not confirmed.');
        onRespond(
          `The reviewed reply was saved to the conversation, but external delivery was not confirmed. Do not resend automatically. Final text: ${JSON.stringify(draft)}`
        );
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to send reply';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = () => {
    setDiscarded(true);
    onRespond("Don't send that — suggest something different.");
  };

  const composerGate =
    !sent && !discarded
      ? {
          sentence: `Send this reply to ${customerName ?? 'the customer'}?`,
          onAccept: handleSend,
          onReject: handleDiscard,
          acceptLabel: sending ? 'Sending…' : 'Send',
          rejectLabel: 'Discard',
          onEdit: () => setEditing((value) => !value),
          editLabel: editing ? 'Done editing' : 'Edit',
          busy: sending,
          acceptDisabled: !draft.trim(),
          acceptDisabledReason: 'Write a reply first.',
        }
      : null;

  if (discarded) {
    return (
      <Badge
        variant="destructive"
        className="gap-1"
        data-testid="claire-card-draft-reply"
      >
        <X className="size-3" />
        Discarded
      </Badge>
    );
  }

  if (sent) {
    return (
      <Badge
        variant="default"
        className="gap-1"
        data-testid="claire-card-draft-reply"
      >
        <Check className="size-3" />
        {deliveryFailed ? 'Saved — delivery not confirmed' : 'Sent'}
      </Badge>
    );
  }

  return (
    <ComposerDecision
      id={id ?? `draft-reply:${conversationId}`}
      gate={composerGate}
    >
      <div
        className="w-full space-y-3 rounded-md border bg-card p-3"
        data-testid="claire-card-draft-reply"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            Draft reply for {customerName ?? 'customer'}
          </span>
          {platform && (
            <Badge variant="outline" className="text-xs">
              {platform.replace(/_/g, ' ')}
            </Badge>
          )}
        </div>

        {/* Body — display or edit */}
        {editing ? (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(3, draft.split('\n').length + 1)}
            className="text-sm"
            aria-label="Edit reply draft"
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{draft}</p>
        )}

        {/* Actions */}
        <DecisionFooter
          hideSentence
          sentence={`Send this reply to ${customerName ?? 'the customer'}?`}
          onAccept={handleSend}
          onReject={handleDiscard}
          acceptLabel={sending ? 'Sending…' : 'Send'}
          rejectLabel="Discard"
          onEdit={() => setEditing((value) => !value)}
          editLabel={editing ? 'Done' : 'Edit'}
          busy={sending}
          acceptDisabled={!draft.trim()}
          acceptDisabledReason="Write a reply first."
        />
      </div>
    </ComposerDecision>
  );
}
